/**
 * Email Sequence Schemas
 *
 * Zod validation schemas for email sequences (multi-step drip campaigns).
 * Issue #360: Juicebox-Style Pipeline Generation
 */

import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export const SequenceStatusSchema = z.enum(['draft', 'active', 'paused', 'archived']);
export type SequenceStatus = z.infer<typeof SequenceStatusSchema>;

export const EnrollmentStatusSchema = z.enum(['active', 'paused', 'completed', 'exited', 'failed']);
export type EnrollmentStatus = z.infer<typeof EnrollmentStatusSchema>;

export const SendTimeTypeSchema = z.enum(['immediate', 'specific_time', 'business_hours']);
export type SendTimeType = z.infer<typeof SendTimeTypeSchema>;

export const SequenceEventTypeSchema = z.enum([
  'scheduled',
  'sent',
  'delivered',
  'opened',
  'clicked',
  'replied',
  'bounced',
  'unsubscribed',
  'complained',
]);
export type SequenceEventType = z.infer<typeof SequenceEventTypeSchema>;

export const ExitReasonSchema = z.enum(['replied', 'clicked', 'bounced', 'manual', 'unsubscribed', 'dnc', 'completed']);
export type ExitReason = z.infer<typeof ExitReasonSchema>;

/**
 * Email Provider (Issue #372 - HubSpot Three-Tier Model)
 * - resend: Marketing emails via Resend API (bulk, compliance-focused)
 * - gmail: Personal inbox via Gmail API (1:1 sales drips, reply detection)
 */
export const EmailProviderSchema = z.enum(['resend', 'gmail']);
export type EmailProvider = z.infer<typeof EmailProviderSchema>;

// ============================================================================
// SEQUENCE STEP SCHEMAS
// ============================================================================

export const SequenceStepInputSchema = z.object({
  stepNumber: z.number().int().positive(),
  name: z.string().max(255).optional(),
  delayDays: z.number().int().min(0).default(0),
  delayHours: z.number().int().min(0).max(23).default(0),
  sendTimeType: SendTimeTypeSchema.default('immediate'),
  sendTime: z
    .string()
    .regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)
    .optional(), // HH:MM format
  sendDays: z.array(z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'])).default([]),
  subject: z.string().min(1).max(500),
  htmlContent: z.string().min(1),
  textContent: z.string().optional(),
});
export type SequenceStepInput = z.infer<typeof SequenceStepInputSchema>;

export const SequenceStepSchema = SequenceStepInputSchema.extend({
  id: z.string().uuid(),
  sequenceId: z.string().uuid(),
  isVariant: z.boolean().default(false),
  variantOf: z.string().uuid().optional(),
  totalSent: z.number().int().default(0),
  opened: z.number().int().default(0),
  clicked: z.number().int().default(0),
  replied: z.number().int().default(0),
  bounced: z.number().int().default(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SequenceStep = z.infer<typeof SequenceStepSchema>;

// ============================================================================
// SEQUENCE SCHEMAS
// ============================================================================

export const CreateSequenceSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  sendingEmail: z.string().email(),
  sendingName: z.string().min(1).max(255),
  replyTo: z.string().email().optional(),
  timezone: z.string().default('America/Los_Angeles'),
  // Email Provider (Issue #372 - HubSpot Three-Tier Model)
  emailProvider: EmailProviderSchema.default('resend'),
  gmailUserId: z.string().uuid().optional(), // Required if emailProvider='gmail'
  gmailThreadMode: z.boolean().default(true), // Thread replies together
  // Enrollment settings
  autoEnroll: z.boolean().default(false),
  enrollmentCriteria: z.record(z.string(), z.unknown()).optional(),
  maxActiveEnrollments: z.number().int().positive().optional(),
  exitOnReply: z.boolean().default(true),
  exitOnClick: z.boolean().default(false),
  exitOnBounce: z.boolean().default(true),
  steps: z.array(SequenceStepInputSchema).optional(),
});
export type CreateSequenceInput = z.infer<typeof CreateSequenceSchema>;

export const UpdateSequenceSchema = z.object({
  sequenceId: z.string().uuid(),
  data: z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).optional(),
    sendingEmail: z.string().email().optional(),
    sendingName: z.string().min(1).max(255).optional(),
    replyTo: z.string().email().optional(),
    timezone: z.string().optional(),
    autoEnroll: z.boolean().optional(),
    enrollmentCriteria: z.record(z.string(), z.unknown()).optional(),
    maxActiveEnrollments: z.number().int().positive().optional(),
    exitOnReply: z.boolean().optional(),
    exitOnClick: z.boolean().optional(),
    exitOnBounce: z.boolean().optional(),
  }),
});
export type UpdateSequenceInput = z.infer<typeof UpdateSequenceSchema>;

export const SequenceSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: SequenceStatusSchema,
  sendingEmail: z.string().email(),
  sendingName: z.string(),
  replyTo: z.string().email().nullable(),
  timezone: z.string(),
  // Email Provider (Issue #372 - HubSpot Three-Tier Model)
  emailProvider: EmailProviderSchema,
  gmailUserId: z.string().uuid().nullable(),
  gmailThreadMode: z.boolean(),
  // Enrollment settings
  autoEnroll: z.boolean(),
  enrollmentCriteria: z.record(z.string(), z.unknown()).nullable(),
  maxActiveEnrollments: z.number().int().nullable(),
  exitOnReply: z.boolean(),
  exitOnClick: z.boolean(),
  exitOnBounce: z.boolean(),
  totalEnrolled: z.number().int(),
  totalCompleted: z.number().int(),
  totalExited: z.number().int(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  steps: z.array(SequenceStepSchema).optional(),
});
export type Sequence = z.infer<typeof SequenceSchema>;

// ============================================================================
// ENROLLMENT SCHEMAS
// ============================================================================

export const EnrollLeadsSchema = z.object({
  sequenceId: z.string().uuid(),
  leadIds: z.array(z.string().uuid()).min(1).max(100),
});
export type EnrollLeadsInput = z.infer<typeof EnrollLeadsSchema>;

export const UnenrollLeadSchema = z.object({
  sequenceId: z.string().uuid(),
  leadId: z.string().uuid(),
  reason: z.string().max(500).optional(),
});
export type UnenrollLeadInput = z.infer<typeof UnenrollLeadSchema>;

export const SequenceEnrollmentSchema = z.object({
  id: z.string().uuid(),
  sequenceId: z.string().uuid(),
  businessId: z.string().uuid(),
  leadId: z.string().uuid(),
  leadEmail: z.string().email(),
  leadName: z.string().nullable(),
  status: EnrollmentStatusSchema,
  currentStep: z.number().int(),
  enrolledAt: z.string().datetime(),
  nextStepAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  exitedAt: z.string().datetime().nullable(),
  exitReason: z.string().nullable(),
  pausedAt: z.string().datetime().nullable(),
  pausedBy: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SequenceEnrollment = z.infer<typeof SequenceEnrollmentSchema>;

// ============================================================================
// LIST / QUERY SCHEMAS
// ============================================================================

export const ListSequencesSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  status: SequenceStatusSchema.optional(),
  search: z.string().optional(),
});
export type ListSequencesInput = z.infer<typeof ListSequencesSchema>;

export const ListEnrollmentsSchema = z.object({
  sequenceId: z.string().uuid(),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  status: EnrollmentStatusSchema.optional(),
});
export type ListEnrollmentsInput = z.infer<typeof ListEnrollmentsSchema>;

export const GetSequenceSchema = z.object({
  sequenceId: z.string().uuid(),
  includeSteps: z.boolean().default(true),
});
export type GetSequenceInput = z.infer<typeof GetSequenceSchema>;

// ============================================================================
// STATS SCHEMAS
// ============================================================================

export const SequenceStatsSchema = z.object({
  sequenceId: z.string().uuid(),
  totalEnrolled: z.number().int(),
  activeEnrollments: z.number().int(),
  completedEnrollments: z.number().int(),
  exitedEnrollments: z.number().int(),
  totalEmailsSent: z.number().int(),
  totalOpened: z.number().int(),
  totalClicked: z.number().int(),
  totalReplied: z.number().int(),
  totalBounced: z.number().int(),
  openRate: z.number(), // Percentage
  clickRate: z.number(),
  replyRate: z.number(),
  bounceRate: z.number(),
  stepStats: z.array(
    z.object({
      stepId: z.string().uuid(),
      stepNumber: z.number().int(),
      sent: z.number().int(),
      opened: z.number().int(),
      clicked: z.number().int(),
      replied: z.number().int(),
      bounced: z.number().int(),
    }),
  ),
});
export type SequenceStats = z.infer<typeof SequenceStatsSchema>;
